import { useCallback, useEffect, useMemo, useState } from "react";
import { APP_NAME, STORAGE_KEY } from "../config";
import { createImportJob, createInitialState, recordAttempt } from "../lib/learningEngine";
import { createAppStorage } from "../platform/sync";
import type { Difficulty, Grade, ImportJob, LearningExercise, LearningPathId, LearningState, ModeId } from "../types";

// Phase 1: lokal. Phase 2: local-first + Sync gegen den Cloudflare-Worker (VITE_API_BASE gesetzt).
const storage = createAppStorage(STORAGE_KEY, import.meta.env.VITE_API_BASE);

function loadState(): LearningState {
  try {
    const raw = storage.load();
    if (!raw) return createInitialState();
    const parsed = JSON.parse(raw) as Partial<LearningState>;
    return {
      ...createInitialState(),
      ...parsed,
      version: 1,
      cards: parsed.cards ?? {},
      sessions: parsed.sessions ?? [],
      importJobs: parsed.importJobs ?? [],
      recoveryDays: parsed.recoveryDays ?? 2,
      streak: parsed.streak ?? 0,
      abilities: parsed.abilities ?? {},
    };
  } catch {
    return createInitialState();
  }
}

export function useLearningState() {
  const [state, setState] = useState<LearningState>(() => loadState());

  useEffect(() => {
    storage.save(JSON.stringify(state));
  }, [state]);

  const acceptBetaRules = useCallback(() => {
    setState((current) => ({
      ...current,
      betaRulesAcceptedAt: current.betaRulesAcceptedAt ?? new Date().toISOString(),
    }));
  }, []);

  const submitAttempt = useCallback(
    (
      exercise: LearningExercise,
      mode: ModeId,
      selected: number[],
      elapsedSeconds: number,
      grade?: Grade,
      difficulty?: Difficulty
    ) => {
      let result: ReturnType<typeof recordAttempt> | undefined;
      setState((current) => {
        result = recordAttempt(current, exercise, mode, selected, elapsedSeconds, grade, difficulty);
        return result.state;
      });
      return result;
    },
    []
  );

  const addImportJob = useCallback(
    (input: { fileName: string; pathId: LearningPathId; markdown: string; sourceManifest: string[] }) => {
      const job = createImportJob(input);
      setState((current) => ({ ...current, importJobs: [job, ...current.importJobs].slice(0, 24) }));
      return job;
    },
    []
  );

  const updateImportJob = useCallback((job: ImportJob) => {
    setState((current) => ({
      ...current,
      importJobs: current.importJobs.map((item) => (item.id === job.id ? job : item)),
    }));
  }, []);

  const exportProgress = useCallback(() => {
    const blob = new Blob([JSON.stringify(state, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${APP_NAME.toLowerCase()}-progress-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }, [state]);

  const resetProgress = useCallback(() => {
    setState(createInitialState());
  }, []);

  return useMemo(
    () => ({
      state,
      acceptBetaRules,
      submitAttempt,
      addImportJob,
      updateImportJob,
      exportProgress,
      resetProgress,
    }),
    [acceptBetaRules, addImportJob, exportProgress, resetProgress, state, submitAttempt, updateImportJob]
  );
}
